'use client';

import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useProspectSelection } from 'src/context/prospects';
import {
  DataGridPremium,
  GridRowId,
  GridRowSelectionModel,
  GridPaginationModel,
  useGridApiRef,
  GridColDef,
  GridSortDirection,
} from '@mui/x-data-grid-premium';
import {
  TableCheckbox,
  TableFooter,
  TableLoadingIndicator,
  TableSettings,
  GroupHeader,
  TableVolumeDrawer,
} from 'src/components';
import useLocalStorage from 'src/hooks/system/useLocalStorage';
import { useGetMappingsQuery } from 'src/api/redux';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import useGetTreatingV2 from 'src/hooks/affiliations/useGetTreatingV2';
import useTableFilterSelections, { resolveVolumeSortField } from 'src/hooks/filters/useTableFilterSelections';
import useEncounterColumns from 'src/hooks/table/useEncounterColumns';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useParams } from 'next/navigation';
import { hasSitesOfCare } from 'src/utils/hasSitesOfCare/hasSitesOfCare';
import NewVolumeHeader from 'src/components/Table/NewVolumeHeader';
import {
  affiliationsTableColumns,
  EncountersPrimaryHeader,
  NewGroupHeader,
  EmptyGroupHeader,
  RoundAddButton,
  EncountersToolbar,
  NewEncountersVolumeCell,
  NewNoVolumeOverlay,
  CustomColumnMenu,
} from '../components';

import { useDebounce } from 'use-debounce';

const EMPTY_COLUMN_IDS: string[] = [];
const EMPTY_ROWS: any[] = [];

const TreatingTable = () => {
  const hasLoaded = useRef(false);
  const prevFlagRef = useRef<boolean | undefined>(undefined);
  const sortModelRef = useRef([]);
  const { id } = useParams<{ id: string; type: string }>();
  const log = useLogging();
  const { setNotification } = useNotification();
  const apiRef = useGridApiRef();
  const auth = useAuthorization();
  const { showEncountersTotals } = useFlags();
  const { entityDetails } = useProfile();

  const {
    columnIds, // this is an array of volume ids [number, number, number]
    isTrending,
    date_range,
    setColumnId,
    isInitialized,
    apiVolumeFilterFormat,
    editingColumnId, // Add this to detect when we're editing vs creating
  } = useTableFilterSelections('treating');

  const { volumeColumns, optionColumns, combinationCohortsFilterSets } =
    useEncounterColumns('treating', {
      setColumnId,
      isTrending,
      columnIds: isInitialized ? columnIds : EMPTY_COLUMN_IDS,
    });

  const {
    state: [, setNewProspects],
  } = useProspectSelection();

  // State for pagination, sorting, and search
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [isExporting, setIsExporting] = useState(false);

  // Add state to track column transitions
  const [isColumnTransitioning, setIsColumnTransitioning] = useState(false);

  // Keep track of all column IDs that have ever been added
  const [allColumnIds, setAllColumnIds] = useState<string[]>([]); // Change to string[]
  const prevColumnIdsRef = useRef<string[]>([]); // Change to string[]

  const [sort, setSort] = useState({
    sort: 'name',
    sort_id: null,
    sort_field: null,
    order: 'asc',
    sortModel: [], // Add sortModel to track DataGrid state
  });

  const [columnVisibilityModel, setColumnVisibilityModel] = useLocalStorage(
    'treatingTable',
    {
      name: true,
      crm_links: true,
      specialty: true,
      rep_name: false,
      tags: false,
      npi: true,
      ccn: false,
      site_id: false,
      address: false,
      street_address: false,
      secondary_addresses_v2: false,
      city: false,
      state: false,
      zip_code: false,
      affiliations: false,
      groups: false,
      actions: false,
      phone: false,
      email: false,
      systems: false,
    }
  );

  const currentYears = useMemo(() => {
    if (!date_range || !date_range?.gte || !date_range?.lte) return [];

    const years = [];
    const { gte, lte } = date_range;
    for (let year = Number(gte); year <= Number(lte); year++) {
      years.push(year);
    }

    return years;
  }, [date_range]);

  // Memoize tableParams to prevent re-renders
  const tableParams = useMemo(
    () => ({
      sort: sort.sort,
      sort_id: sort.sort_id,
      sort_field: sort.sort_field,
      order: sort.order,
      // Don't include sortModel in API params
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      q: debouncedSearch || null,
    }),
    [
      sort.sort,
      sort.sort_id,
      sort.sort_field,
      sort.order,
      paginationModel.page,
      paginationModel.pageSize,
      debouncedSearch,
    ]
  );

  const {
    data: treatingData,
    isLoading,
    isFetching,
    exportTreating,
  } = useGetTreatingV2({
    provider_id: id,
    tableParams,
  });

  // CCN / Site ID columns are only relevant when the list contains a site of care.
  const hasSites = useMemo(
    () => hasSitesOfCare(treatingData?.results),
    [treatingData]
  );

  const { data: allMappings } = useGetMappingsQuery({
    sort: 'label',
    order: 'asc',
    page: 0,
    pageSize: 100,
  });

  const crmTypes = useMemo(() => {
    return Array.from(
      new Set(
        allMappings?.results
          ?.filter((mapping) => mapping.enabled)
          .map((mapping) => mapping.crm?.toLowerCase())
      )
    );
  }, [allMappings]);

  // Handle when columnIds change after initial render - set default sort to desc
  useEffect(() => {
    const columnIdsChanged =
      JSON.stringify(prevColumnIdsRef.current) !== JSON.stringify(columnIds);

    // Recompute the default volume sort when the flag resolves/changes so ordering
    // stays aligned with the (encounters vs claims) value the cells now display.
    const flagChanged = prevFlagRef.current !== showEncountersTotals;
    prevFlagRef.current = showEncountersTotals;

    if (
      (!hasLoaded.current || flagChanged) &&
      columnIds?.length > 0 &&
      isInitialized
    ) {
      hasLoaded.current = true;
      const firstColumnId = columnIds[0];

      let sort_field = null;
      if (apiVolumeFilterFormat && firstColumnId) {
        // Use the full composite key to lookup in apiVolumeFilterFormat
        sort_field = resolveVolumeSortField(
          apiVolumeFilterFormat[firstColumnId],
          showEncountersTotals
        );
      }

      const newSortModel = [{ field: `here_${firstColumnId}`, sort: 'desc' }];
      sortModelRef.current = newSortModel;
      setSort({
        sort: 'volume_at_location', // Use volume_at_location as default for treating table
        sort_id: null, // Don't set sort_id for volume sorting - code set ID goes in filter_set
        sort_field: sort_field,
        order: 'desc',
        sortModel: newSortModel, // Set initial sort model to here (volume_at_location) column
      });
    } else if (columnIdsChanged && editingColumnId && columnIds?.length > 0) {
      // We just applied an edit (editingColumnId is still set and columnIds changed)
      // Find the new column ID that replaced the editingColumnId
      let editedColumnId: string | null = null;

      // The edited column is the one that's different at the position where editingColumnId was
      const oldIndex = prevColumnIdsRef.current.indexOf(editingColumnId);
      if (oldIndex >= 0 && oldIndex < columnIds.length) {
        // The edited column should be at the same position but with a different ID
        const newColumnAtPosition = columnIds[oldIndex];
        if (newColumnAtPosition !== editingColumnId) {
          editedColumnId = newColumnAtPosition;
        }
      }

      // If we couldn't find it by position, look for any new column ID
      if (!editedColumnId) {
        editedColumnId =
          columnIds.find((id) => !prevColumnIdsRef.current.includes(id)) ||
          null;
      }

      if (editedColumnId) {
        // Set sort to the edited column
        let sort_field = null;
        if (apiVolumeFilterFormat && editedColumnId) {
          sort_field = resolveVolumeSortField(
            apiVolumeFilterFormat[editedColumnId],
            showEncountersTotals
          );
        }

        const editedSortModel = [{ field: `here_${editedColumnId}`, sort: 'desc' }];
        sortModelRef.current = editedSortModel;
        setSort({
          sort: 'volume_at_location', // Use volume_at_location as default for treating table
          sort_id: null, // Don't set sort_id for volume sorting - code set ID goes in filter_set
          sort_field: sort_field,
          order: 'desc',
          sortModel: editedSortModel, // Set sort model to here (volume_at_location) column of edited column
        });

        // Apply sort to the DataGrid
        if (apiRef.current) {
          apiRef.current.setSortModel([
            {
              field: `here_${editedColumnId}`,
              sort: 'desc' as GridSortDirection,
            },
          ]);
        }
      }
    } else if (columnIds?.length === 0) {
      hasLoaded.current = null;
    }

    // Always update the previous columnIds reference
    if (columnIds) {
      prevColumnIdsRef.current = [...columnIds];
    }
  }, [
    columnIds,
    apiVolumeFilterFormat,
    showEncountersTotals,
    isInitialized,
    editingColumnId,
    apiRef,
  ]);

  useEffect(() => {
    if (!apiRef.current?.getSelectedRows) return;
    setNewProspects(Array.from(apiRef.current.getSelectedRows().values()));

    return () => setNewProspects([]);
  }, [selectionModel]);

  /**
   * Creates a visibility map for columns based on user permissions
   * @param crmTypes The available CRM types
   * @returns A map of column visibility settings
   */
  const serializeColumnVisibility = () => {
    const visibilityMap = {};

    // Hide CRM column if user does not have access to CRM
    visibilityMap['crm_links'] = crmTypes.length > 0;

    // CCN / Site ID only when the list contains a site of care.
    visibilityMap['ccn'] = hasSites;
    visibilityMap['site_id'] = hasSites;

    return visibilityMap;
  };

  // In the useEffect for columnVisibilityModel (around line 192-219) - Add explicit visibility for placeholder:
  useEffect(() => {
    // Use a callback to ensure we're working with the latest state
    setColumnVisibilityModel((columnVisibilityState) => {
      const newVisibility = {
        ...columnVisibilityState,
        ...serializeColumnVisibility(),
        add_code_set_placeholder: true, // Force show the placeholder column for the add button
      };

      // Hide all volume columns first
      if (allColumnIds?.length > 0) {
        allColumnIds.forEach((id) => {
          newVisibility[`here_${id}`] = false;
          newVisibility[`total_${id}`] = false;
        });
      }

      // Then show only current active columns
      if (columnIds?.length > 0) {
        columnIds.forEach((id) => {
          newVisibility[`here_${id}`] = true;
          newVisibility[`total_${id}`] = true;
        });
      }

      return newVisibility;
    });
  }, [
    volumeColumns,
    optionColumns,
    columnIds,
    allColumnIds,
    currentYears,
    isTrending,
    hasSites,
    setColumnVisibilityModel,
  ]);

  // Ultra-simplified effect to handle column changes
  useEffect(() => {
    // Temporarily disable column resizing and autosize
    setIsColumnTransitioning(true);

    // Update previous column IDs for next change
    prevColumnIdsRef.current = [...columnIds];

    // Update column visibility model immediately
    setColumnVisibilityModel((columnVisibilityState) => {
      const newVisibility = { ...columnVisibilityState };

      // Hide all volume columns first
      if (allColumnIds?.length > 0) {
        allColumnIds.forEach((id) => {
          newVisibility[`here_${id}`] = false;
          newVisibility[`total_${id}`] = false;
        });
      }

      // Then show only current active columns
      if (columnIds?.length > 0) {
        columnIds.forEach((id) => {
          newVisibility[`here_${id}`] = true;
          newVisibility[`total_${id}`] = true;
        });
      }

      return newVisibility;
    });

    // Force refresh the grid and re-enable column resizing
    if (apiRef.current) {
      apiRef.current.forceUpdate();

      // Re-enable column resizing after a short delay
      setTimeout(() => {
        setIsColumnTransitioning(false);
      }, 100);
    }
  }, [columnIds, apiRef, allColumnIds, setColumnVisibilityModel]);

  // Update allColumnIds whenever columnIds changes
  useEffect(() => {
    if (columnIds && columnIds.length > 0) {
      setAllColumnIds((prev: string[]) => {
        // Add type annotation
        // Add any new columnIds that aren't already in allColumnIds
        const newIds = columnIds.filter((id) => !prev.includes(id));
        if (newIds.length > 0) {
          return [...prev, ...newIds];
        }
        return prev;
      });
    }
  }, [columnIds]);

  /**
   * Creates volume columns for the treating table
   * @param allColumnIds All column IDs (including historical ones)
   * @param columnIds Current active column IDs
   * @returns Array of volume columns
   */
  const createVolumeColumns = (
    allColumnIds: string[] = [], // Change to string[]
    columnIds: string[] = [] // Change to string[]
  ): GridColDef[] => {
    // Create columns for all column IDs (including historical ones)
    // This ensures the DataGrid always has access to all column definitions
    const allColumns =
      allColumnIds && allColumnIds?.length > 0
        ? allColumnIds.flatMap((id, index) => [
            {
              field: `here_${id}`,
              headerName: 'Here',
              // Use fixed width properties to prevent transition errors
              width: 100,
              minWidth: 100,
              maxWidth: 100,
              flex: undefined, // Remove flex when using fixed width
              renderHeader: (params) => <NewVolumeHeader {...params} />,
              renderCell: (params) => (
                <NewEncountersVolumeCell searchId={id} {...params} />
              ),
              headerClassName:
                index === 0
                  ? 'secondary-first-column-header secondary-column-header'
                  : 'secondary-column-header',
              cellClassName:
                index === 0
                  ? 'secondary-first-column-cell secondary-column-cell'
                  : 'secondary-column-cell',
            },
            {
              field: `total_${id}`,
              headerName: 'Total',
              // Use fixed width properties to prevent transition errors
              width: 100,
              minWidth: 100,
              maxWidth: 100,
              flex: undefined, // Remove flex when using fixed width
              renderHeader: (params) => <NewVolumeHeader {...params} />,
              renderCell: (params) => (
                <NewEncountersVolumeCell searchId={id} {...params} />
              ),
              headerClassName: 'secondary-column-header',
              cellClassName: 'secondary-column-cell',
            },
          ])
        : [];

    // Always add a placeholder column for the "Add Code Set" button
    const placeholderColumn = {
      field: 'add_code_set_placeholder',
      headerName: '',
      sortable: false,
      // Use a wider width when there are no applied volumes
      width: columnIds?.length > 0 ? 56 : 200,
      headerClassName:
        columnIds?.length > 0
          ? 'secondary-column-header text-center'
          : 'secondary-first-column-header secondary-column-header text-center',
      cellClassName:
        columnIds?.length > 0
          ? 'secondary-column-cell text-center'
          : 'secondary-first-column-cell secondary-column-cell text-center',
      type: 'string' as const,
    };

    return [...allColumns, placeholderColumn];
  };

  /**
   * Creates the combined column grouping model for the treating table
   * @param columnIds Current active column IDs
   * @param allColumnIds All column IDs (including historical ones)
   * @param combinationCohortsFilterSets Filter sets for the combination cohorts
   * @param onTransitionStart Callback for when a column transition starts
   * @param onRefresh Callback for when the grid needs to be refreshed
   * @returns The column grouping model
   */
  const createColumnGroupingModel = (
    columnIds: string[] = [], // Change to string[]
    allColumnIds: string[] = [], // Change to string[]
    combinationCohortsFilterSets: any[] = [],
    onTransitionStart: () => void,
    onRefresh: () => void
  ) => {
    // Main columns group
    const mainColumnsGroup = {
      groupId: 'Provider Info',
      description: 'Provider Information',
      renderHeaderGroup: (params) => {
        return <GroupHeader {...params} isVolume={false} />;
      },
      children: [
        ...affiliationsTableColumns.map((col) => ({ field: col.field })),
      ],
    };

    // Create groups for all volume column IDs (including historical ones)
    // This ensures the DataGrid always has access to all column group definitions
    const volumeGroups =
      allColumnIds && allColumnIds?.length > 0
        ? allColumnIds
            .map((compositeId) => {
              const numericId = Number(compositeId.split('_')[0]); // Extract base numeric ID
              const search = combinationCohortsFilterSets.find(
                (s) => s.id === numericId
              );
              if (!search) return null;
              return {
                groupId: compositeId, // Use full composite ID as string for groupId
                headerName: search?.label,
                renderHeaderGroup: (params) => {
                  return <NewGroupHeader {...params} tableType="treating" />;
                },
                children: [
                  { field: `here_${compositeId}` },
                  { field: `total_${compositeId}` },
                ],
              };
            })
            .filter(Boolean) // Filter out null/undefined values
        : [];

    // Add an appropriate button based on whether columns exist
    const addButtonGroup = {
      groupId: 'add_new_code_set',
      headerName: '',
      renderHeaderGroup: () =>
        columnIds && columnIds.length > 0 ? (
          <RoundAddButton
            location="treating"
            onTransitionStart={onTransitionStart}
            onRefresh={onRefresh}
          />
        ) : (
          <EmptyGroupHeader
            location="treating"
            onTransitionStart={onTransitionStart}
            onRefresh={onRefresh}
          />
        ),
      children: [{ field: 'add_code_set_placeholder' }],
    };

    return [mainColumnsGroup, ...volumeGroups, addButtonGroup];
  };

  /**
   * Creates the combined columns for the treating table
   * @param allColumnIds All column IDs (including historical ones)
   * @param columnIds Current active column IDs
   * @returns The combined columns
   */
  const createCombinedColumns = (
    allColumnIds: string[] = [], // Change to string[]
    columnIds: string[] = [] // Change to string[]
  ) => {
    const volumeColumns = createVolumeColumns(allColumnIds, columnIds);

    // Put the main columns first and volume columns last
    // This ensures volume columns are positioned correctly from the start
    // and prevents them from appearing on the left briefly
    return [...affiliationsTableColumns, ...volumeColumns];
  };

  /**
   * Gets the fields that should be pinned to the right
   * @param columnIds The current column IDs
   * @returns An array of field names to pin to the right
   */
  const getRightPinnedFields = (columnIds: string[] = []) => {
    // Change to string[]
    // Get volume column fields from current columnIds (not all historical ones)
    const volumeFields =
      columnIds && columnIds?.length > 0
        ? columnIds.flatMap((id) => [`here_${id}`, `total_${id}`])
        : [];

    // Add the placeholder column
    const placeholderField = ['add_code_set_placeholder'];

    // Combine all fields that should be pinned to the right
    return [...volumeFields, ...placeholderField];
  };

  /**
   * Handles exporting the treating table data
   * @param type The export type
   */
  const handleExportList = async (type: string) => {
    const visibleColumns = Object.entries(columnVisibilityModel)
      .filter(([, value]) => value === true)
      .map(([key]) => key)
      // Filter out the old here_XXXX and total_XXXX column formats
      .filter(
        (column) =>
          !column.startsWith('here_') &&
          !column.startsWith('total_') &&
          !column.includes('add_code_set_placeholder')
      );

    // Add volume columns based on the type of filtering used
    const volumeColumns = [];

    if (
      apiVolumeFilterFormat &&
      Object.keys(apiVolumeFilterFormat).length > 0
    ) {
      // We have code sets/encounters - use new column format
      // Check what types of volumes we have (claims vs patients)
      const hasClaimsVolumes = Object.values(apiVolumeFilterFormat).some(
        (volume) => volume?.type?.toLowerCase() === 'claims'
      );
      const hasPatientsVolumes = Object.values(apiVolumeFilterFormat).some(
        (volume) => volume?.type?.toLowerCase() === 'patients'
      );

      if (hasClaimsVolumes) {
        volumeColumns.push('claims_at_location', 'claims_total');
      }
      if (hasPatientsVolumes) {
        volumeColumns.push('patients_at_location', 'patients_total');
      }
    }

    const finalColumns = visibleColumns.concat(volumeColumns);
    setIsExporting(true);

    const fileName = `${entityDetails?.name}_AffiliatedTreating`;

    try {
      await exportTreating({
        fileFormat: type,
        columns: finalColumns,
        fileName,
      });
      setNotification({
        title: 'Success',
        message:
          'Export initiated - You will receive an email when it is ready for download',
        type: 'success',
      });
      log.event('Export', {
        type: 'affiliations',
        source: 'All Affiliated - Treating',
        columns: visibleColumns,
        count: treatingData?.count,
      });
    } catch (err: any) {
      log.exception(`Error exporting All Affiliated - Treating: ${err}`);

      setNotification({
        title: 'Error',
        message: err?.error_message || 'Error exporting, please try again.',
        type: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Handles sort model changes for the treating table
   * @param model The sort model from DataGrid
   */
  const handleSortChange = useCallback(
    (model) => {
      // Early return if model hasn't changed
      if (JSON.stringify(model) === JSON.stringify(sortModelRef.current)) {
        return;
      }
      sortModelRef.current = model;

      const { field, sort: sortDirection } = model?.[0] || {};
      if (model?.length && !field) return;

      // Helper to get treating-specific sort info
      const getTreatingSortInfo = (field) => {
        if (field?.includes('here_')) {
          const searchId = field.replace('here_', '');

          return {
            sortValue: 'volume_at_location',
            sort_field: resolveVolumeSortField(
              apiVolumeFilterFormat?.[searchId],
              showEncountersTotals
            ),
          };
        }
        if (field?.includes('total_')) {
          const searchId = field.replace('total_', '');

          return {
            sortValue: 'volume_total',
            sort_field: resolveVolumeSortField(
              apiVolumeFilterFormat?.[searchId],
              showEncountersTotals
            ),
          };
        }

        return { sortValue: field || 'name', sort_field: null };
      };

      const { sortValue, sort_field } = getTreatingSortInfo(field);

      // Handle empty model case (like NewListsTable does)
      let finalSortValue = sortValue;
      let final_sort_field = sort_field;
      let finalSortModel = model;

      // Extract sort_id for treating table (composite key from here_/total_ fields)
      let final_sort_id = null;
      if (field?.includes('here_') || field?.includes('total_')) {
        final_sort_id = field.replace(/^(here_|total_)/, ''); // Remove prefix to get composite key
      }

      if (!model?.length && columnIds.length > 0) {
        // Default to "Here" (volume_at_location) for the treating table so the API sort
        // and the grid's sort model stay in sync with the initial default sort.
        finalSortValue = 'volume_at_location';
        final_sort_field = resolveVolumeSortField(
          apiVolumeFilterFormat[columnIds[0]],
          showEncountersTotals
        );
        final_sort_id = columnIds[0]; // Use the full composite key as sort_id
        finalSortModel = [{ field: `here_${columnIds[0]}`, sort: 'desc' }];
      }

      const newSortState = {
        sort: finalSortValue,
        sort_id: final_sort_id, // Use the full composite key for volume columns
        sort_field: final_sort_field,
        order: sortDirection || 'desc',
        sortModel: finalSortModel,
      };

      setSort(newSortState);
    },
    [apiVolumeFilterFormat, columnIds, showEncountersTotals]
  );

  /**
   * Handles page changes for the treating table
   * @param e The event
   * @param newPage The new page number
   */
  const handlePageChange = (e, newPage) => {
    setPaginationModel((prev) => ({ ...prev, page: newPage }));
  };

  /**
   * Handles page size changes for the treating table
   * @param e The event
   */
  const handlePageSizeChange = (e) => {
    setPaginationModel((prev) => ({
      ...prev,
      pageSize: parseInt(e.target.value, 10),
      page: 0,
    }));
  };

  /**
   * Handles search changes for the treating table
   * @param value The search value
   */
  const handleSearch = (value) => {
    setSearch(value);
    // ensure we are on the 1st page for searches
    if (paginationModel.page !== 0) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  };

  /**
   * Handles column visibility model changes
   * @param newModel The new column visibility model
   */
  const handleColumnVisibilityChange = (newModel) => {
    // Temporarily disable column resizing and autosize
    setIsColumnTransitioning(true);

    // Update column visibility immediately
    setColumnVisibilityModel(newModel);

    // Force refresh the grid
    if (apiRef.current) {
      apiRef.current.forceUpdate();

      // Re-enable column resizing after a short delay
      setTimeout(() => {
        setIsColumnTransitioning(false);
      }, 100);
    }
  };

  // Get fields that should be pinned to the right
  const rightPinnedFields = useMemo(() => {
    return getRightPinnedFields(columnIds);
  }, [columnIds]);

  // Combined grouping model for both main columns and volume columns
  const combinedGroupingModel = useMemo(() => {
    return createColumnGroupingModel(
      columnIds,
      allColumnIds,
      combinationCohortsFilterSets,
      () => setIsColumnTransitioning(true),
      () => apiRef.current?.forceUpdate()
    );
  }, [columnIds, allColumnIds, combinationCohortsFilterSets]);

  const combinedColumns = useMemo(() => {
    const baseColumns = createCombinedColumns(allColumnIds, columnIds);

    // Add proper sortingOrder to regular columns to fix tri-state behavior
    return baseColumns.map((col) => {
      // Check if this is a volume column (would have custom renderHeader)
      const isVolumeColumn = col.renderHeader && col.field.includes('_');

      if (!isVolumeColumn) {
        // Regular columns: remove null state and custom renderHeader
        return {
          ...col,
          sortingOrder: ['asc', 'desc'] as any, // Remove null state to prevent third click reset
          renderHeader: undefined, // Remove custom header to restore default DataGrid sorting behavior
        };
      }

      return { ...col, sortingOrder: ['desc', 'asc'] as any };
    });
  }, [allColumnIds, columnIds]);

  const userCanExport = auth.hasPermission('export.site_providers.all');

  return (
    <div className="relative border border-neutral-300 rounded-lg flex flex-col p-0 bottom-0 h-full max-h-224 w-full min-w-0 overflow-hidden">
      <EncountersPrimaryHeader title="Treating" />
      <div className="w-full h-full overflow-hidden flex flex-col min-w-0">
        <DataGridPremium
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          // Disable column resizing during transitions to prevent computedColumnWidth errors
          disableColumnResize={isColumnTransitioning}
          // Disable auto-sizing during transitions
          disableAutosize={isColumnTransitioning}
          apiRef={apiRef}
          rows={treatingData?.results || EMPTY_ROWS}
          rowCount={treatingData?.count || 0}
          columns={combinedColumns}
          getRowId={(row) => row.provider_id}
          hideFooter={true}
          columnGroupingModel={combinedGroupingModel}
          pinnedColumns={{
            right: rightPinnedFields,
            left: [], // keeps from flashing if we are explicit
          }}
          checkboxSelection
          sx={{
            // Base table styling
            border: 'none',
            borderRadius: 0,
            overflow: 'hidden',
            height: '100%',
            flex: 1,

            // Fix for pinned columns alignment
            '& .MuiDataGrid-columnHeaders': {
              position: 'relative', // Ensure headers have a positioning context
              paddingRight: '0px',
            },

            // Explicitly position volume columns on the right side of the grid
            '& .MuiDataGrid-columnHeader.secondary-column-header, & .MuiDataGrid-cell.secondary-column-cell':
              {
                right: '0 !important',
                left: 'auto !important',
                position: 'relative !important',
              },

            // Pinned column headers (v7: native sticky, no position overrides)
            '& .MuiDataGrid-columnHeader--pinnedRight': {
              background: 'var(--secondary-outlined-ActiveBg, #D6F6F1)',
            },

            // Pinned cells (v7: native sticky, no position overrides)
            '& .MuiDataGrid-cell--pinnedRight': {
              background: 'var(--secondary-outlined-ActiveBg, #D6F6F1)',
            },

            // Column group header styling
            '& .MuiDataGrid-columnGroupHeader': {
              backgroundColor: columnIds?.length === 0 ? '#D6F6F1' : 'inherit',
              borderLeft:
                columnIds?.length === 0 ? '1px solid #00C6A9' : 'none',
            },

            // Center the button in the empty group header
            '& .MuiDataGrid-columnGroup': {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            },

            // Center text in the placeholder column
            '& .text-center': {
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            },

            // Virtual scroller
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto',
              overflowX: 'auto', // Allow horizontal scrolling
              minHeight: '300px', // Ensure minimum height even when empty
            },

            // Empty state styling
            '& .MuiDataGrid-overlay': {
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },

            // Primary columns hover effect
            '& .MuiDataGrid-row:hover, & .Mui-hovered': {
              backgroundColor: 'rgba(204, 204, 194, 0.13)',
              '&.Mui-hovered': {
                backgroundColor: 'rgba(204, 204, 194, 0.13)',
              },
            },

            // Secondary columns - base styling
            '& .secondary-column-header, & .secondary-column-cell': {
              background: 'var(--secondary-outlined-ActiveBg, #D6F6F1)',
              borderRight: '#00C6A9 1px solid',
            },

            // Secondary column headers specific styling
            '& .secondary-column-header': {
              borderBottom: '#00C6A9 2px solid !important', // Restored 2px with !important to ensure visibility
              boxShadow: 'none',
            },

            // Override any conflicting styles from MUI
            '& .MuiDataGrid-columnHeaders .secondary-column-header, & .MuiDataGrid-columnHeadersInner .secondary-column-header':
              {
                borderBottom: '#00C6A9 2px solid !important', // Ensure this is not overridden
              },

            // Secondary column cells specific styling
            // '& .secondary-column-cell': {
            //   borderBottom: '#00C6A9 1px solid',
            //   borderLeft: columnIds.length === 0 ? '#00C6A9 1px solid' : 'none',
            // },

            // First column in secondary section gets left border
            '& .secondary-first-column-cell': {
              borderLeft: columnIds.length > 0 ? '#00C6A9 1px solid' : 'none',
            },

            // Add left border to the first pinned column header and cell
            '& .MuiDataGrid-columnHeader--pinnedRight:first-of-type, & .MuiDataGrid-cell--pinnedRight:first-of-type':
              {
                borderLeft: '#00C6A9 1px solid',
              },

            // Column separators in secondary section
            '& .secondary-column-header .MuiDataGrid-columnSeparator': {
              color: '#00C6A9',
            },

            // Hover effect for secondary columns
            '& .MuiDataGrid-row:hover .secondary-column-cell, & .Mui-hovered .secondary-column-cell':
              {
                backgroundColor: '#D6F6F1',
              },

            // Hover effect for pinned cells
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight, & .Mui-hovered .MuiDataGrid-cell--pinnedRight':
              {
                backgroundColor: '#D6F6F1',
              },
          }}
          initialState={{
            columns: { columnVisibilityModel },
            pagination: { paginationModel: { page: 0, pageSize: 50 } },
            // Add pinnedColumns to initialState to ensure they're applied from the start
            pinnedColumns: {
              right: rightPinnedFields,
              left: [],
            },
          }}
          slots={{
            toolbar: EncountersToolbar,
            baseCheckbox: TableCheckbox,
            columnMenu: CustomColumnMenu,
            noRowsOverlay: () => (
              <NewNoVolumeOverlay
                columnIds={columnIds
                  .map((id) => {
                    const baseId = id.split('_')[0];
                    return parseInt(baseId, 10);
                  })
                  .filter((id) => !isNaN(id))}
              />
            ),
            ...TableSettings.slots,
          }}
          slotProps={{
            columnsManagement: {
              disableShowHideToggle: true,
            },
            toolbar: {
              location: 'treating',
              onSearch: handleSearch,
              // Export button props
              onExport: handleExportList,
              isExporting: isExporting,
              // Column button props
              columns: affiliationsTableColumns,
              columnVisibilityModel: columnVisibilityModel,
              setColumnVisibility: setColumnVisibilityModel,
              // Feature flags
              showExportButton: userCanExport,
              showAddToListButton: true,
            },
          }}
          // Pagination
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          // Sorting
          sortingMode="server"
          sortModel={sort.sortModel || []}
          onSortModelChange={handleSortChange}
          // Column visibility with immediate updates and transition handling
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          // Rows
          rowHeight={75}
          onRowSelectionModelChange={setSelectionModel}
          rowSelectionModel={selectionModel}
        />
      </div>
      <TableFooter
        page={paginationModel.page}
        pageSize={paginationModel.pageSize}
        rowCount={treatingData?.count || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
      {(isLoading || isFetching) && <TableLoadingIndicator />}
      <TableVolumeDrawer location="treating" />
    </div>
  );
};

export default TreatingTable;
